import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DatasetSelector from './DatasetSelector';
import UserMenu from './UserMenu';
import ThemeToggle from './ThemeToggle';
import './Header.css';
import { Link } from 'react-router-dom';
/**
 * Header Component
 * 
 * Main navigation header with view selection, dataset management,
 * user menu, and signal type controls.
 */
const Header = ({
  demoMode = false,
  curatorPreviewEnabled = false,
  storageScope = 'guest',
  datasets,
  currentDataset,
  onDatasetChange,
  onRecordingFormat,
  onUploadClick,
  onDatasetDelete,
  onDatasetVisibility,
  selectedView,
  onViewChange,
  selectedSignalType,
  onSignalTypeChange
}) => {
  const storageKey = `spikescope_header_collapsed:${storageScope}`;
  const readCollapsed = () => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  };
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    try { setCollapsed(localStorage.getItem(storageKey) === 'true'); } catch { setCollapsed(false); }
  }, [storageKey]);
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* Still works without storage. */ }
  };
  return (
    <div className={`header${collapsed ? ' header-collapsed' : ''}`}>
      <button type="button" className="header-fold-toggle" onClick={toggleCollapsed}
        aria-expanded={!collapsed} aria-controls="dashboard-header-content"
        aria-label={collapsed ? 'Show top banner' : 'Fold top banner'}
        title={collapsed ? 'Show top banner' : 'Fold top banner'}>
        <svg className="header-tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={collapsed ? 'm6 9 6 6 6-6' : 'm6 15 6-6 6 6'} />
        </svg>
        <span className="header-tab-text">Banner</span>
      </button>
      <div id="dashboard-header-content" className="header-content" hidden={collapsed}>
      <div className="header-left">
        <Link to="/" className="dashboard-title-link">
   <div className="landing-nav-brand">
          <div className="landing-nav-logo">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="url(#bolt-grad)" />
              <defs>
                <linearGradient id="bolt-grad" x1="3" y1="2" x2="21" y2="22">
                  <stop stopColor="#40e0d0" />
                  <stop offset="1" stopColor="#0d9488" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span className="landing-nav-title">SpikeScope</span>
        </div>
</Link>
{demoMode && <span className="playground-badge">Playground</span>}
      </div>

      <div className="header-controls">
        {selectedView === 'signal' && (
          <div className="view-selector-container">
            <label htmlFor="signal-type-select">Signal Type:</label>
            <select
              id="signal-type-select"
              className="view-selector"
              value={selectedSignalType}
              onChange={(e) => onSignalTypeChange(e.target.value)}
            >
              <option value="raw">Raw Data</option>
              <option value="filtered">Filtered Data</option>
              <option value="spikes">Detected Spikes</option>
            </select>
          </div>
        )}

        <div className="view-selector-container">
          <label htmlFor="view-select">View:</label>
          <select
            id="view-select"
            className="view-selector"
            value={selectedView}
            onChange={(e) => onViewChange(e.target.value)}
          >
            <option value="signal">Signal View</option>
            <option value="clusters">Cluster View</option>
            <option value="multipanel">Canvas View</option>
            {curatorPreviewEnabled && <option value="curator-preview">Curator Preview</option>}
            <option value="runtime">Runtime Analysis View</option>
          </select>
        </div>

        <DatasetSelector
          datasets={datasets}
          currentDataset={currentDataset}
          onDatasetChange={onDatasetChange}
          onDatasetDelete={onDatasetDelete}
          onDatasetVisibility={onDatasetVisibility}
          onRecordingFormat={onRecordingFormat}
        />
        {!demoMode && (

        <button 
          className="upload-button-header" 
          onClick={onUploadClick}
          title="Upload new dataset"
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </button>
        )}
       <ThemeToggle />
       {demoMode ? (
  <Link to="/login" className="demo-signin-button" title="Sign in">
    Sign In
  </Link>
) : (
  <UserMenu />
)}
      </div>
      </div>
    </div>
  );
};

Header.propTypes = {
  /** Array of available datasets */
  datasets: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number,
    sizeFormatted: PropTypes.string,
  })).isRequired,
  /** Currently selected dataset name */
  currentDataset: PropTypes.string,
  /** Callback when dataset is changed */
  onDatasetChange: PropTypes.func.isRequired,
  /** Callback when upload button is clicked */
  onUploadClick: PropTypes.func.isRequired,
  /** Callback when dataset delete is requested */
  onDatasetDelete: PropTypes.func.isRequired,
  /** Currently selected view */
  selectedView: PropTypes.oneOf(['signal', 'clusters', 'multipanel', 'runtime', 'curator-preview']).isRequired,
  /** Callback when view is changed */
  onViewChange: PropTypes.func.isRequired,
  /** Currently selected signal type (for signal view) */
  selectedSignalType: PropTypes.oneOf(['raw', 'filtered', 'spikes']),
  /** Callback when signal type is changed */
  onSignalTypeChange: PropTypes.func,
};

Header.defaultProps = {
  currentDataset: null,
  selectedSignalType: 'raw',
  onSignalTypeChange: () => {},
};

export default Header;
